/**
 * Configuración de la aplicación
 * Aquí se definen las funcionalidades y secciones disponibles en la app
 */

import { EventMapContentConfig } from '../models/EventMap';
import { SponsorsContentConfig } from '../models/Sponsor';

export interface AppFeature {
  enabled: boolean;
  icon?: string;
  label?: string;
  order?: number;
}

export interface SessionSyncFeature extends AppFeature {
  syncToBackend?: boolean;
}

export interface NetworkingField {
  key: string;
  label: string;
  type: 'text' | 'email' | 'phone' | 'url';
  required: boolean;
  visible: boolean;
  order: number;
}

export type AuthenticationMode = 'cedula' | 'email_password';
export type AppUpdateMode = 'disabled' | 'silent' | 'optional' | 'required';

export interface AppReleaseConfig {
  /** Estrategia aplicada cuando existe un build más nuevo en el servidor. */
  updateMode: AppUpdateMode;
  /** Build objetivo. Cero utiliza automáticamente el build desplegado. */
  latestBuild: number;
  /** Build mínimo permitido. Cero no impone un mínimo. */
  minimumBuild: number;
  /** Frecuencia de consulta del manifiesto y de la política remota. */
  checkIntervalMinutes: number;
  /** Texto mostrado cuando la actualización requiere interacción. */
  message: string;
}
export type AdministrativeSectionKey =
  | 'membership'
  | 'associationEvents'
  | 'participation'
  | 'accountStatement'
  | 'digitalCard'
  | 'benefits'
  | 'news'
  | 'certificates';

export interface AdministrativeSectionConfig extends AppFeature {
  route: string;
  description: string;
}

export interface AppGradientConfig {
  start: string;
  end: string;
  angle: number;
}

export interface AppBrandingConfig {
  name: string;
  shortName: string;
  logo?: string;
  appIcon?: string;
  menuImage?: {
    url: string;
    alt?: string;
  };
  colors: {
    primary: string;
    primaryLight?: string;
    primaryDark?: string;
    uiPrimary?: string;
    uiSecondary?: string;
    uiAccent?: string;
    secondary: string;
    tertiary: string;
    accent: string;
    success: string;
    warning: string;
    danger: string;
    favorite: string;
    background: string;
    surface: string;
    textLight: string;
    textDark: string;
    textSecondary: string;
    border: string;
    cardBackground: string;
    modalBackground?: string;
    dark?: string;
    medium?: string;
    light?: string;
    neutral50?: string;
    neutral100?: string;
    neutral200?: string;
    neutral300?: string;
    neutral400?: string;
    neutral500?: string;
    neutral600?: string;
    neutral700?: string;
    neutral800?: string;
    neutral900?: string;
    twitter?: string;
    google?: string;
    vimeo?: string;
    facebook?: string;
    instagram?: string;
    daySelectorText?: string;
    daySelectorSelectedBackground?: string;
    daySelectorSelectedText?: string;
    daySelectorIndicator?: string;
    daySelectorDarkIndicator?: string;
    sessionCardBackground?: string;
    sessionCardText?: string;
    sessionCardSecondaryText?: string;
    eventInfoAccent?: string;
    eventInfoAccentText?: string;
    selectedTabBackground?: string;
    floatingMenuBackground?: string;
    floatingMenuDarkBackground?: string;
  };
  gradients: {
    primary: AppGradientConfig;
    secondary: AppGradientConfig;
    accent: AppGradientConfig;
    background: AppGradientConfig;
    navigation: AppGradientConfig;
    tabBar: AppGradientConfig;
    header: AppGradientConfig;
    card: AppGradientConfig;
    button: AppGradientConfig;
    success: AppGradientConfig;
    blue?: AppGradientConfig;
    green?: AppGradientConfig;
    warm?: AppGradientConfig;
    cool?: AppGradientConfig;
    sunset?: AppGradientConfig;
    ocean?: AppGradientConfig;
    daySelector?: AppGradientConfig;
  };
}

export interface AppTutorialSlideConfig {
  image?: string;
  title: string;
  description?: string;
  buttonLabel?: string;
}

export interface AppTutorialConfig {
  enabled: boolean;
  showOnce: boolean;
  skipLabel: string;
  backgroundImage?: string;
  slides: AppTutorialSlideConfig[];
}

export type DeepPartial<T> = {
  [Property in keyof T]?: T[Property] extends Array<infer Item>
    ? Array<Item>
    : T[Property] extends object
      ? DeepPartial<T[Property]>
      : T[Property];
};

export interface AppConfig {
  // Política de actualización del código publicada por el backend.
  release: AppReleaseConfig;

  // Funcionalidades principales
  features: {
    schedule: AppFeature;
    speakers: AppFeature;
    questions: AppFeature;
    favorites: AppFeature;
    calendar: AppFeature;
    badge: AppFeature;
    networking: AppFeature;
    map: AppFeature;
    sponsors: AppFeature;
    about: AppFeature;
    sessionSync: SessionSyncFeature;
    requiredSessionLock: AppFeature;
    agendaRestrictions: AppFeature;
    carnetDigital: AppFeature & {
      mandatoryEventCode?: string; // Código del evento mandatorio para consultas del carnet digital
    };
  };

  // Configuración de autenticación
  auth: {
    mode?: AuthenticationMode; // Por ahora la app decide qué formulario utiliza
    allowAccountActivation?: boolean;
    allowPublicSchedule?: boolean; // Ausente/true conserva agenda pública; false exige sesión en la app.
    multievent: boolean; // Permite login con múltiples eventos
    client: string; // ID del cliente por defecto
    defaultEventID?: string; // ID del evento por defecto cuando multievent es false
    defaultRoute: string; // Ruta a la que se redirige después de seleccionar un evento o cerrar la selección
    showPasswordAsText: boolean; // Si true, muestra la contraseña como texto visible; si false, como password oculto
  };

  // Configuración de networking
  networking: {
    enabled: boolean;
    qrCodePrefix: string; // Prefijo para identificar QRs de la app
    fields: NetworkingField[];
    maxScansPerUser: number;
    allowDuplicateScans: boolean;
  };

  // Configuración de tabs del menú inferior
  bottomTabs: {
    schedule: AppFeature;
    networking: AppFeature;
    speakers: AppFeature;
    badge: AppFeature;
    sponsors: AppFeature;
  };

  // Configuración de preguntas
  questions: {
    enabled: boolean;
    allowAnonymous: boolean;
    requireModeration: boolean;
    maxQuestionsPerUser: number;
  };

  // Configuración de favoritos
  favorites: {
    enabled: boolean;
    syncWithFirebase: boolean;
    maxFavorites: number;
  };

  // Contenido del módulo opcional de patrocinadores.
  sponsors: SponsorsContentConfig;

  // Plano interactivo opcional del evento.
  eventMap: EventMapContentConfig;

  // Secciones administrativas opcionales para asociaciones y colegios.
  administrative: {
    enabled: boolean;
    organizationName: string;
    useDummyData: boolean;
    dataEndpoint?: string;
    sections: Record<AdministrativeSectionKey, AdministrativeSectionConfig>;
  };

  branding: AppBrandingConfig;
  tutorial: AppTutorialConfig;
}

// Configuración por defecto de la aplicación
export const defaultAppConfig: AppConfig = {
  release: {
    updateMode: 'disabled',
    latestBuild: 0,
    minimumBuild: 0,
    checkIntervalMinutes: 15,
    message: 'Hay una nueva versión de la aplicación disponible.'
  },

  features: {
    schedule: {
      enabled: true,
      icon: 'calendar-outline',
      label: 'Agenda',
      order: 1
    },
    questions: {
      enabled: true,
      icon: 'help-circle-outline',
      label: 'Preguntas',
      order: 3
    },
    favorites: {
      enabled: true,
      icon: 'heart-outline',
      label: 'Favoritos',
      order: 4
    },
    calendar: {
      enabled: true,
      icon: 'calendar',
      label: 'Calendario',
      order: 5
    },
    badge: {
      enabled: false,
      icon: 'card-outline',
      label: 'Gafete',
      order: 6
    },
    networking: {
      enabled: true,
      icon: 'people-circle-outline',
      label: 'Networking',
      order: 7
    },
     speakers: {
      enabled: true,
      icon: 'people-outline',
      label: 'Conferencistas',
      order: 2
    },
    map: {
      enabled: false,
      icon: 'map-outline',
      label: 'Mapa del evento',
      order: 8
    },
    sponsors: {
      enabled: false,
      icon: 'ribbon-outline',
      label: 'Patrocinadores',
      order: 9
    },
    about: {
      enabled: true,
      icon: 'information-circle-outline',
      label: 'Acerca de',
      order: 10
    },
    sessionSync: {
      enabled: false,
      syncToBackend: false,
      icon: 'sync-outline',
      label: 'Sincronizar sesiones',
      order: 12
    },
    requiredSessionLock: {
      enabled: false,
      icon: 'lock-closed-outline',
      label: 'Bloqueo de sesiones requeridas',
      order: 13
    },
    agendaRestrictions: {
      enabled: false,
      icon: 'shield-checkmark-outline',
      label: 'Aplicar restricciones de agenda en app',
      order: 14
    },
    carnetDigital: {
      enabled: false,
      icon: 'card-outline',
      label: 'Carnet Digital',
      order: 11,
      mandatoryEventCode: '89885FF2' // Código del evento base para consultas del carnet digital
    }
  },

  // Configuración de autenticación
  auth: {
    mode: 'email_password', // Cambiar a email_password para builds con acceso Firebase
    allowAccountActivation: true,
    allowPublicSchedule: true,
    multievent: false, // Se fuerza a true automáticamente cuando administrative.enabled está activo
    client: '8', // Cliente por defecto
    defaultEventID: '05546B0E', // Evento actual que se carga inicialmente; multievento conserva acceso al historial
    defaultRoute: '/tabs/schedule', // Ruta por defecto después de seleccionar evento (ej: '/tabs/schedule', '/tabs/speakers', '/account')
    showPasswordAsText: false // Por defecto, ocultar la contraseña (false = password, true = text)
  },
  
  bottomTabs: {
    schedule: {
      enabled: true,
      icon: 'calendar-outline',
      label: 'Agenda',
      order: 1
    },
    networking: {
      enabled: true,
      icon: 'people-circle-outline',
      label: 'Networking',
      order: 3
    },
    speakers: {
      enabled: true,
      icon: 'people-outline',
      label: 'Speakers',
      order: 2
    },
    badge: {
      enabled: false,
      icon: 'card-outline',
      label: 'Gafete',
      order: 4
    },
    sponsors: {
      enabled: false,
      icon: 'diamond-outline',
      label: 'Patrocinadores',
      order: 5
    }
  },
  
  networking: {
    enabled: true,
    qrCodePrefix: 'ESIMPOSIO_USER_',
    maxScansPerUser: 500,
    allowDuplicateScans: false,
    fields: [
      {
        key: 'name',
        label: 'Nombre',
        type: 'text',
        required: true,
        visible: true,
        order: 1
      },
      {
        key: 'email',
        label: 'Correo Electrónico',
        type: 'email',
        required: true,
        visible: true,
        order: 2
      },
      {
        key: 'phone',
        label: 'Teléfono',
        type: 'phone',
        required: false,
        visible: true,
        order: 3
      },
      {
        key: 'company',
        label: 'Empresa',
        type: 'text',
        required: false,
        visible: true,
        order: 4
      },
      {
        key: 'position',
        label: 'Cargo',
        type: 'text',
        required: false,
        visible: true,
        order: 5
      },
      {
        key: 'linkedin',
        label: 'LinkedIn',
        type: 'url',
        required: false,
        visible: false,
        order: 6
      }
    ]
  },
  
  questions: {
    enabled: true,
    allowAnonymous: false,
    requireModeration: true,
    maxQuestionsPerUser: 10
  },
  
  favorites: {
    enabled: true,
    syncWithFirebase: true,
    maxFavorites: 50
  },

  sponsors: {
    title: 'Juntos hacemos posible este evento',
    description: 'Conoce a las organizaciones que apoyan esta experiencia.',
    chipLabel: 'Patrocinador',
    emptyMessage: 'No hay patrocinadores disponibles para este evento.',
    dataEndpoint: undefined,
    items: [
      {
        id: 'sponsor-001',
        name: 'Grupo Salud Integral',
        description: 'Impulsando innovación para una atención más humana y accesible.',
        level: 'Patrocinador principal',
        website: 'https://example.com/grupo-salud',
        email: 'contacto@gruposalud.example',
        phone: '+507 300-0101',
        color: '#A452B7',
        order: 1,
        isActive: true
      },
      {
        id: 'sponsor-002',
        name: 'BioLab Panamá',
        description: 'Soluciones científicas para profesionales de la salud.',
        level: 'Patrocinador',
        website: 'https://example.com/biolab',
        email: 'info@biolab.example',
        phone: '+507 300-0102',
        color: '#3BB54B',
        order: 2,
        isActive: true
      },
      {
        id: 'sponsor-003',
        name: 'Tecnología Clínica',
        description: 'Tecnología que conecta conocimiento, datos y personas.',
        level: 'Patrocinador',
        website: 'https://example.com/tecnologia-clinica',
        email: 'hola@tecnologiaclinica.example',
        phone: '+507 300-0103',
        color: '#562873',
        order: 3,
        isActive: true
      }
    ]
  },

  eventMap: {
    title: 'Mapa del evento',
    searchPlaceholder: 'Buscar stands...',
    allCategoriesLabel: 'Todos',
    emptyMessage: 'No hay stands disponibles para este evento.',
    backgroundImage: '/assets/img/floor-plan.svg',
    dataEndpoint: undefined,
    mapSettings: {
      minZoom: 0.5,
      maxZoom: 3,
      initialZoom: 1,
      enablePan: true,
      enableZoom: true
    },
    stands: [
      {
        id: 'stand-tech-001',
        name: 'TechCorp Solutions',
        description: 'Soluciones tecnológicas innovadoras para empresas modernas.',
        category: 'Tecnología',
        website: 'https://example.com/techcorp',
        email: 'info@techcorp.example',
        phone: '+507 300-0201',
        position: { x: 14, y: 60 },
        size: { width: 14, height: 11 },
        color: '#A452B7',
        isActive: true,
        order: 1
      },
      {
        id: 'stand-edu-002',
        name: 'EduFuture Academy',
        description: 'Plataforma educativa con experiencias digitales innovadoras.',
        category: 'Educación',
        website: 'https://example.com/edufuture',
        email: 'contacto@edufuture.example',
        position: { x: 31, y: 60 },
        size: { width: 14, height: 11 },
        color: '#3F5C50',
        isActive: true,
        order: 2
      },
      {
        id: 'stand-health-003',
        name: 'HealthTech Innovations',
        description: 'Tecnología médica para una atención más conectada.',
        category: 'Salud',
        website: 'https://example.com/healthtech',
        email: 'info@healthtech.example',
        position: { x: 48, y: 60 },
        size: { width: 14, height: 11 },
        color: '#3BB54B',
        isActive: true,
        order: 3
      },
      {
        id: 'stand-biz-004',
        name: 'Business Pro Solutions',
        description: 'Consultoría empresarial y soluciones de gestión.',
        category: 'Negocios',
        website: 'https://example.com/business-pro',
        position: { x: 65, y: 60 },
        size: { width: 14, height: 11 },
        color: '#2A3B80',
        isActive: true,
        order: 4
      },
      {
        id: 'stand-innov-005',
        name: 'Innovation Hub',
        description: 'Centro de innovación y desarrollo de nuevas iniciativas.',
        category: 'Innovación',
        website: 'https://example.com/innovation-hub',
        position: { x: 22, y: 74 },
        size: { width: 16, height: 11 },
        color: '#B75A2B',
        isActive: true,
        order: 5
      },
      {
        id: 'sponsor-001',
        name: 'Grupo Salud Integral',
        description: 'Impulsando innovación para una atención más humana y accesible.',
        category: 'Patrocinadores',
        website: 'https://example.com/grupo-salud',
        email: 'contacto@gruposalud.example',
        position: { x: 43, y: 74 },
        size: { width: 14, height: 11 },
        color: '#A452B7',
        isActive: true,
        order: 6
      },
      {
        id: 'sponsor-002',
        name: 'BioLab Panamá',
        description: 'Soluciones científicas para profesionales de la salud.',
        category: 'Patrocinadores',
        website: 'https://example.com/biolab',
        email: 'info@biolab.example',
        position: { x: 60, y: 74 },
        size: { width: 14, height: 11 },
        color: '#3BB54B',
        isActive: true,
        order: 7
      }
    ]
  },

  administrative: {
    enabled: false,
    organizationName: 'Asociación profesional',
    // Posteriormente puede cambiarse a false y configurar dataEndpoint.
    useDummyData: true,
    dataEndpoint: undefined,
    sections: {
      membership: {
        enabled: true,
        icon: 'person-circle-outline',
        label: 'Mi membresía',
        order: 1,
        route: '/administrative/membership',
        description: 'Estado y datos de afiliación'
      },
      associationEvents: {
        enabled: true,
        icon: 'calendar-outline',
        label: 'Eventos institucionales',
        order: 2,
        route: '/administrative/events',
        description: 'Actividades, historial y agenda'
      },
      participation: {
        enabled: true,
        icon: 'checkbox-outline',
        label: 'Participar',
        order: 3,
        route: '/administrative/participation',
        description: 'Votaciones, encuestas y convocatorias'
      },
      accountStatement: {
        enabled: true,
        icon: 'wallet-outline',
        label: 'Estado de cuenta',
        order: 4,
        route: '/administrative/account',
        description: 'Cuotas, pagos y movimientos'
      },
      digitalCard: {
        enabled: true,
        icon: 'card-outline',
        label: 'Carné de asociado',
        order: 5,
        route: '/administrative/card',
        description: 'Identificación y validación QR'
      },
      benefits: {
        enabled: true,
        icon: 'star-outline',
        label: 'Beneficios',
        order: 6,
        route: '/administrative/benefits',
        description: 'Convenios y descuentos'
      },
      news: {
        enabled: true,
        icon: 'notifications-outline',
        label: 'Noticias',
        order: 7,
        route: '/administrative/news',
        description: 'Comunicaciones institucionales'
      },
      certificates: {
        enabled: true,
        icon: 'ribbon-outline',
        label: 'Certificados',
        order: 8,
        route: '/administrative/certificates',
        description: 'Historial de formación'
      }
    }
  },

  branding: {
    name: 'XVII Congreso Internacional de Dirección de Proyectos',
    shortName: 'eSimposio',
    logo: 'assets/img/esimposio-conecta-logo.png',
    appIcon: 'assets/img/appicon.png',
    colors: {
      primary: '#3F5C50',
      secondary: '#1F571F',
      tertiary: '#5F7FFF',
      accent: '#B75A2B',
      success: '#4CD964',
      warning: '#FFC409',
      danger: '#EB445A',
      favorite: '#69BB7B',
      background: '#FFFFFF',
      surface: '#F7FAFC',
      textLight: '#FFFFFF',
      textDark: '#333333',
      textSecondary: '#888888',
      border: '#E2E8F0',
      cardBackground: '#FFFFFF'
    },
    gradients: {
      primary: { start: '#3F5C50', end: '#1F571F', angle: 135 },
      secondary: { start: '#1F571F', end: '#5F7FFF', angle: 135 },
      accent: { start: '#B75A2B', end: '#6EE87A', angle: 135 },
      background: { start: '#3F5C50', end: '#1F571F', angle: 135 },
      navigation: { start: '#3F5C50', end: '#1F571F', angle: 135 },
      tabBar: { start: '#1F571F', end: '#5F7FFF', angle: 135 },
      header: { start: '#3F5C50', end: '#1F571F', angle: 135 },
      card: { start: '#B75A2B', end: '#6EE87A', angle: 135 },
      button: { start: '#2A3B80', end: '#1F571F', angle: 135 },
      success: { start: '#3F5C50', end: '#B75A2B', angle: 135 }
    }
  },

  tutorial: {
    enabled: true,
    showOnce: true,
    skipLabel: 'Saltar',
    backgroundImage: 'https://esimposio.com/appimelcf/fondo-tutorial.jpg',
    slides: [
      {
        image: 'assets/img/ica-slidebox-img-1.png',
        title: 'Bienvenido a la app del Congreso Centroamericano de Gastroenterología 2026',
        description: 'Aquí podrá ver su carnet digital, información de eventos y otra información importante.'
      },
      {
        image: 'assets/img/ica-slidebox-img-2.png',
        title: 'Añada al calendario',
        description: 'Las sesiones y actividades que más le llamen la atención.'
      },
      {
        image: 'assets/img/ica-slidebox-img-3.png',
        title: 'Preguntas',
        description: 'Recuerde votar o realizar sus preguntas en las sesiones y actividades.'
      },
      {
        image: 'assets/img/ica-slidebox-img-4.png',
        title: 'Bienvenido(a)',
        buttonLabel: 'Continuar'
      }
    ]
  }
};

let effectiveAppConfig: AppConfig = defaultAppConfig;

const isPlainObject = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const mergeConfiguration = <T>(base: T, override: DeepPartial<T>): T => {
  if (!isPlainObject(base) || !isPlainObject(override)) {
    return (override === undefined ? base : override) as T;
  }

  const result: Record<string, unknown> = { ...base };
  Object.entries(override).forEach(([key, overrideValue]) => {
    if (overrideValue === undefined) return;

    const baseValue = (base as Record<string, unknown>)[key];
    result[key] = isPlainObject(baseValue) && isPlainObject(overrideValue)
      ? mergeConfiguration(baseValue, overrideValue)
      : overrideValue;
  });

  return result as T;
};

export const applyRemoteAppConfig = (remoteConfig: DeepPartial<AppConfig>): AppConfig => {
  effectiveAppConfig = mergeConfiguration(defaultAppConfig, remoteConfig);
  return effectiveAppConfig;
};

export const resetAppConfig = (): void => {
  effectiveAppConfig = defaultAppConfig;
};

export const getAppConfig = (): AppConfig => {
  return effectiveAppConfig;
};

// Función para verificar si una funcionalidad está habilitada
export const isFeatureEnabled = (feature: keyof AppConfig['features']): boolean => {
  const config = getAppConfig();
  return config.features[feature]?.enabled || false;
};

// Preguntas conserva dos bloques por compatibilidad con perfiles anteriores:
// `features.questions` controla la presencia del módulo y `questions` sus
// opciones internas. Cualquiera de los dos puede desactivarlo.
export const isQuestionsEnabled = (): boolean => {
  const config = getAppConfig();
  return Boolean(config.features.questions?.enabled && config.questions?.enabled);
};

// Función para obtener los campos de networking configurados
export const getNetworkingFields = (): NetworkingField[] => {
  const config = getAppConfig();
  return config.networking.fields
    .filter(field => field.visible)
    .sort((a, b) => a.order - b.order);
};

// Función para obtener los tabs del menú inferior habilitados
export const getEnabledBottomTabs = () => {
  const config = getAppConfig();
  return Object.entries(config.bottomTabs)
    .filter(([key, tab]) => tab.enabled && (key !== 'sponsors' || config.features.sponsors.enabled))
    .sort(([_, a], [__, b]) => (a.order || 0) - (b.order || 0))
    .map(([key, tab]) => ({ key, ...tab }));
};

// Función para obtener la configuración de autenticación
export const getAuthConfig = () => {
  const config = getAppConfig();
  const multievent = config.administrative.enabled || config.auth.multievent;

  return {
    ...config.auth,
    multievent
  };
};

// Función para verificar si multievent está habilitado
export const isMultieventEnabled = (): boolean => {
  return getAuthConfig().multievent;
};

// Función para obtener el cliente por defecto
export const getDefaultClient = (): string => {
  const config = getAppConfig();
  return config.auth.client;
};

// Función para obtener el evento por defecto
export const getDefaultEventID = (): string | undefined => {
  return getAuthConfig().defaultEventID;
};

export const isAdministrativeSectionEnabled = (section: AdministrativeSectionKey): boolean => {
  const config = getAppConfig();
  return config.administrative.enabled && Boolean(config.administrative.sections[section]?.enabled);
};

export const getEnabledAdministrativeSections = () => {
  const config = getAppConfig();
  if (!config.administrative.enabled) return [];

  return Object.entries(config.administrative.sections)
    .filter(([_, section]) => section.enabled)
    .sort(([_, a], [__, b]) => (a.order || 0) - (b.order || 0))
    .map(([key, section]) => ({ key: key as AdministrativeSectionKey, ...section }));
};
